import React, { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import {
  CurrencyFormatters,
  getRewardText,
  roundToTwoDecimals,
  SplitPay,
  SummaryRow,
  useDeviceTypes,
  PaymentMethodRadio,
  ActionButton,
  B2BSpinner,
} from "halifax";
import { Box, Checkbox, Typography } from "@material-ui/core";
import {
  CreditCard,
  FiatPrice,
  PaymentV2Enum,
  RewardsAccount,
  RewardsPrice,
} from "redmond";
import { getConfigTenant } from "@capone/common";
import { Payment, PaymentOpaqueValue } from "@b2bportal/purchase-api";

import styles from "./MultiPaymentOptions.module.scss";
import { fetchTravelWalletSummary } from "../../../../api/v1/travel-wallet/fetchTravelWalletSummary";
import { convertRewardsAmountToUsd } from "../../../../api/v1/rewards/convertRewardsAmountToUsd";
import { convertUsdToRewards } from "../../../../api/v1/rewards/convertUsdToRewards";
import { confirmCopy } from "../../../../constants";

interface PaymentOptionProps {
  customAmount: number;
  setCustomAmount: (amount: number) => void;
  rewardsPaymentInFiatCurrency: FiatPrice | null;
  splitPayCoversTotal: boolean;
  onSelect: () => void;
  rewardAccount?: RewardsAccount;
  paymentLabel?: JSX.Element;
  disabled?: boolean;
  isSplittingCredits?: boolean;
  coversTotalCostNotification?: string;
  maxCreditAmount?: number;
}

const PaymentOption = ({
  disabled,
  paymentLabel,
  customAmount = 0,
  setCustomAmount,
  splitPayCoversTotal,
  rewardAccount,
  onSelect,
  rewardsPaymentInFiatCurrency,
  isSplittingCredits,
  coversTotalCostNotification = "",
  maxCreditAmount,
}: PaymentOptionProps) => {
  const { matchesMobile } = useDeviceTypes();

  return (
    <>
      <Box
        className={clsx(styles["select-checkbox-container"], {
          [styles.disabled]: disabled,
        })}
      >
        <Checkbox
          checked={customAmount > 0}
          disabled={disabled}
          onClick={onSelect}
        />
        <Box
          className={styles["select-checkbox-info-container"]}
          onClick={!disabled ? onSelect : undefined}
        >
          <Typography className={styles["select-travel-wallet-item-text"]}>
            {paymentLabel}
          </Typography>
        </Box>
      </Box>
      {customAmount > 0 && (
        <SplitPay
          account={rewardAccount}
          splitPayCoversTotal={splitPayCoversTotal}
          customAmount={customAmount}
          setCustomAmount={setCustomAmount}
          rewardsPaymentInFiatCurrency={rewardsPaymentInFiatCurrency}
          isSplittingCredits={isSplittingCredits}
          titles={{
            applyText: "Apply",
            editButtonText: "Edit amount",
            saveButtonText: "Save",
            cancelButtonText: "Cancel",
            customAmountTooHighError:
              "Invalid amount. Enter an amount that is lower then your available balance.",
            customAmountGenericError:
              "Invalid amount. Please enter a valid amount.",
            saveNotification: "To apply this amount, Save before you continue.",
            coversTotalCostNotification,
          }}
          isMobile={matchesMobile}
          disabled={disabled}
          maxCreditAmount={maxCreditAmount}
        />
      )}
    </>
  );
};

interface MultiPaymentOptions {
  totalPrice: number;
  rewards: RewardsAccount[];
  cardPaymentMethods: CreditCard[];
  currencyCode: string;
  currencySymbol: string;
  newRewardsPrice: (cashAmt: number) => RewardsPrice;
  onSubmit: (payments: PaymentOpaqueValue[]) => Promise<void>;
  isSubmitting?: boolean;
  submitButtonText: string;
  enableSubmitButton: boolean;
}

export const MultiPaymentOptions = ({
  totalPrice,
  rewards,
  cardPaymentMethods,
  currencyCode,
  currencySymbol,
  newRewardsPrice,
  onSubmit,
  isSubmitting,
  submitButtonText,
  enableSubmitButton,
}: MultiPaymentOptions) => {
  const { matchesMobile } = useDeviceTypes();

  const [maxCreditAvailable, setMaxCreditAvailable] = useState<number>(0);
  const [rewardsAmountInRewardsCurrency, setRewardsAmountInRewardsCurrency] =
    useState<number>(0);
  const [rewardToApply, setRewardToApply] = useState<number>(0);
  const [creditToApply, setCreditToApply] = useState<number>(0);
  const [creditId, setCreditId] = useState("");
  const [selectedCardPaymentMethod, setSelectedCardPaymentMethod] = useState<
    CreditCard | undefined
  >(undefined);

  useEffect(() => {
    const fetchWalletCredit = async () => {
      const data = await fetchTravelWalletSummary();
      // -amount indicates available credit
      if (data?.credit?.amount.amount && data.credit.amount.amount < 0) {
        setMaxCreditAvailable(data.credit.amount.amount * -1);
        setCreditId(data.credit.id);
      }
    };

    fetchWalletCredit();
  }, []);

  useEffect(() => {
    if (cardPaymentMethods.length > 0) {
      // select first card as default
      setSelectedCardPaymentMethod(cardPaymentMethods[0]);
    }
  }, [cardPaymentMethods]);

  const { rewardAccount, rewardPaymentLabel, creditPaymentLabel } =
    useMemo(() => {
      if (rewards.length === 0) {
        return {
          rewardAccount: undefined,
        };
      }

      return {
        rewardAccount: rewards[0], // should be the largest rewards account
        rewardPaymentLabel: (
          <div>
            Apply {rewards[0].productDisplayName} Rewards:{" "}
            <span className={styles.balance}>
              {getRewardText({ reward: rewards[0].rewardsBalance })}
            </span>
          </div>
        ),
        creditPaymentLabel: (
          <div>
            Apply Travel Credits:{" "}
            <span className={styles.balance}>
              {CurrencyFormatters.get(currencyCode).format(maxCreditAvailable)}
            </span>
          </div>
        ),
      };
    }, [rewards, currencyCode, maxCreditAvailable]);

  const handleCreditSelection = useCallback(() => {
    if (creditToApply > 0) {
      setCreditToApply(0);
    } else {
      setCreditToApply(
        maxCreditAvailable < totalPrice ? maxCreditAvailable : totalPrice,
      );

      if (rewardToApply > 0) {
        setRewardToApply(0);
        setRewardsAmountInRewardsCurrency(0);
      }
    }
  }, [totalPrice, creditToApply, rewardToApply, maxCreditAvailable]);

  const convertUsdToRewardsAmount = useCallback(
    async (amount: number) => {
      if (!rewardAccount?.accountReferenceId) return 0;

      const rewardsPrice = await convertUsdToRewards({
        amount,
        accountReferenceId: rewardAccount.accountReferenceId,
      });
      return rewardsPrice.value;
    },
    [rewardAccount],
  );

  const convertRewardsToUsd = useCallback(
    async (amount: number) => {
      if (!rewardAccount?.accountReferenceId) return 0;

      const fiatPrice = await convertRewardsAmountToUsd({
        rewardsAmount: amount,
        accountReferenceId: rewardAccount.accountReferenceId,
      });
      return fiatPrice.value;
    },
    [rewardAccount],
  );

  const handleRewardSelection = useCallback(async () => {
    if (rewardToApply > 0) {
      setRewardToApply(0);
      setRewardsAmountInRewardsCurrency(0);
    } else {
      const totalRewardCharge = roundToTwoDecimals(
        creditToApply ? totalPrice - creditToApply : totalPrice,
      );

      // if rewards balance is less than total price, apply rewards balance instead
      const newRewardTotal =
        (rewardAccount?.rewardsCashEquivalent?.value ?? 0) < totalRewardCharge
          ? rewardAccount?.rewardsCashEquivalent.value ?? 0
          : totalRewardCharge;

      setRewardToApply(newRewardTotal);
      const convertedAmount = await convertUsdToRewardsAmount(newRewardTotal);
      setRewardsAmountInRewardsCurrency(convertedAmount);
    }
  }, [
    totalPrice,
    rewardToApply,
    creditToApply,
    convertUsdToRewardsAmount,
    rewardAccount,
  ]);

  const handleRewardsAmountChange = useCallback(
    async (rewardsAmount: number) => {
      const convertedAmountInUsd = await convertRewardsToUsd(rewardsAmount);
      setRewardToApply(convertedAmountInUsd);
      setRewardsAmountInRewardsCurrency(rewardsAmount);
    },
    [convertRewardsToUsd],
  );

  const totalPayable = useMemo(
    () => totalPrice - rewardToApply - creditToApply,
    [totalPrice, rewardToApply, creditToApply],
  );

  const handleSubmitClick = useCallback(() => {
    const { accountReferenceId, rewardsBalance } = rewards[0];
    const currency = confirmCopy.DEFAULT_CASH_CURRENCY;

    const userPayments: PaymentOpaqueValue[] = [];

    if (creditToApply > 0) {
      userPayments.push({
        type: Payment.TravelWalletCredit,
        value: {
          offerId: creditId,
          description: "TravelWalletCredit",
          paymentAmount: {
            fiatValue: {
              amount: roundToTwoDecimals(creditToApply),
              currency,
            },
          },
          PaymentV2: PaymentV2Enum.TravelWalletCredit,
        },
      });
    }

    if (rewardToApply > 0) {
      userPayments.push({
        type: Payment.Rewards,
        value: {
          paymentAmount: {
            rewardsAccountId: accountReferenceId,
            fiatValue: {
              amount: roundToTwoDecimals(rewardToApply),
              currency,
            },
            rewardsPrice: {
              value: roundToTwoDecimals(rewardsAmountInRewardsCurrency),
              currency: rewardsBalance.currency,
            },
          },
          PaymentV2: PaymentV2Enum.Rewards,
        },
      });
    }

    if (totalPayable > 0 && selectedCardPaymentMethod) {
      const selectedRewardAccount = rewards.find(
        (reward) => reward.lastFour === selectedCardPaymentMethod?.last4,
      );
      userPayments.push({
        type: Payment.Card,
        value: {
          paymentId: selectedCardPaymentMethod.id,
          accountReferenceId:
            selectedRewardAccount?.accountReferenceId || accountReferenceId,
          paymentAmount: {
            currency,
            amount: roundToTwoDecimals(totalPayable),
          },
          last4: selectedCardPaymentMethod.last4,
          PaymentV2: PaymentV2Enum.UserCard,
        },
      });
    }

    onSubmit(userPayments);
  }, [
    rewardToApply,
    creditToApply,
    rewards,
    totalPayable,
    rewardsAmountInRewardsCurrency,
    selectedCardPaymentMethod,
  ]);

  return (
    <>
      <Box className={styles["payment-options-container"]}>
        <PaymentOption
          onSelect={handleCreditSelection}
          disabled={maxCreditAvailable === 0 || rewardToApply === totalPrice}
          customAmount={creditToApply}
          setCustomAmount={setCreditToApply}
          paymentLabel={creditPaymentLabel}
          splitPayCoversTotal={creditToApply === totalPrice}
          rewardAccount={rewardAccount}
          rewardsPaymentInFiatCurrency={{
            currencyCode,
            currencySymbol,
            value: creditToApply,
          }}
          isSplittingCredits
          coversTotalCostNotification={
            creditToApply === totalPrice
              ? "Your travel credits covers the total cost of your booking."
              : ""
          }
          maxCreditAmount={Math.min(
            maxCreditAvailable,
            Math.max(0, roundToTwoDecimals(totalPrice - rewardToApply))
          )}
        />
        <PaymentOption
          onSelect={handleRewardSelection}
          disabled={
            rewardAccount == null ||
            rewardAccount.rewardsBalance.value === 0 ||
            creditToApply === totalPrice
          }
          customAmount={rewardsAmountInRewardsCurrency}
          setCustomAmount={handleRewardsAmountChange}
          paymentLabel={rewardPaymentLabel}
          splitPayCoversTotal={rewardToApply + creditToApply === totalPrice}
          rewardAccount={rewardAccount}
          rewardsPaymentInFiatCurrency={{
            currencyCode,
            currencySymbol,
            value: rewardToApply,
          }}
          coversTotalCostNotification={
            creditToApply > 0
              ? "Your travel credits and rewards covers the total cost of your booking."
              : "Your rewards covers the total cost of your booking."
          }
        />
        {rewardToApply + creditToApply !== totalPrice &&
          cardPaymentMethods.length > 1 &&
          cardPaymentMethods.map((savedPayment) => {
            const rewardsAccount = rewards.find(
              (account) => account.lastFour === savedPayment.last4,
            );

            return (
              <PaymentMethodRadio
                key={savedPayment.id}
                account={rewardsAccount}
                matchingSavedPayment={savedPayment}
                tenant={getConfigTenant()}
                cardEndingIn="Ending in"
                selectedPaymentHopperId={selectedCardPaymentMethod?.id}
                selectPaymentMethod={() => {
                  setSelectedCardPaymentMethod(savedPayment);
                }}
                isMobile={matchesMobile}
                hideDeletePaymentMethod
                // unused props, not optional but can be undefined
                disallowSelect={false}
                disabled={false}
                earnValuesByRewardAcctId={undefined}
                isVCN={false}
                product={undefined}
                expiredCard={undefined}
                isNonFinancialUser={false}
                showEarnValue={undefined}
                removePaymentMethod={() => {}}
                paymentMethodDisabled={() => false}
              />
            );
          })}
      </Box>
      {creditToApply > 0 && (
        <Box className="pricing-summary-section-container">
          <SummaryRow
            summary={{
              fiatPrice: {
                currencyCode,
                currencySymbol,
                value: -1 * creditToApply,
              },
              label: "Travel credits applied",
              type: "custom",
            }}
          />
        </Box>
      )}
      {rewardToApply > 0 && (
        <Box className="pricing-summary-section-container">
          <SummaryRow
            summary={{
              fiatPrice: {
                currencyCode,
                currencySymbol,
                value: -1 * rewardToApply,
              },
              label: `${rewards[0].productDisplayName} Rewards applied`,
              rewardsPrice: newRewardsPrice(-1 * rewardToApply),
              type: "custom",
            }}
          />
        </Box>
      )}
      <Box className="pricing-summary-section-container">
        <SummaryRow
          summary={{
            fiatPrice: {
              currencyCode,
              currencySymbol,
              value: totalPayable,
            },
            label: confirmCopy.TOTAL_PAYABLE,
            lastFour: totalPayable > 0 && selectedCardPaymentMethod?.last4,
            type: "payment",
          }}
          titles={{
            rewardsAccountText: (lastFour?: string) =>
              lastFour ? `Ending in ${lastFour}` : `Amount due:`,
            rewardsAppliedText: (accountName: string) =>
              `${accountName} Rewards applied:`,
            totalText: "Total",
          }}
        />
      </Box>
      <ActionButton
        className="submit-request-btn"
        disabled={isSubmitting || !enableSubmitButton}
        message={
          isSubmitting ? (
            <B2BSpinner className="processing-submission-spinner" />
          ) : (
            submitButtonText
          )
        }
        onClick={handleSubmitClick}
      />
    </>
  );
};
